import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { Box, Button, CircularProgress, Grid, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import AuthContext from '../../context/authContext';
import useAgent from '../../hooks/useAgent';
import useStatisticJob from '../../hooks/useStatisticJob';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import StatisticViewShell from '../ui/StatisticViewShell';
import { STATISTIC_TYPE_AGENT_SESSION } from '../../constants/statisticTypes';
import {
  getBarTooltipCursor,
  getCartesianGridStroke,
  getPieSliceStroke,
  getRechartsAxisStroke,
  getRechartsLegendBarProps,
  getRechartsLegendPieProps,
  getRechartsTooltipProps,
  getStatisticsKpiHelperColor,
  getStatisticsKpiLabelColor,
  getStatisticsPageRootStyle,
  getStatisticsSurfaceBackground,
} from '../../util/chartTheme';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const defaultStartDate = dayjs().startOf('month').format('YYYY-MM-DD');
const defaultEndDate = dayjs().endOf('month').format('YYYY-MM-DD');

const COLORS = [
  '#4caf50',
  '#2196f3',
  '#ff9800',
  '#9c27b0',
  '#f44336',
  '#00bcd4',
  '#ffc107',
  '#8bc34a',
  '#ff5722',
];

const decimalFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const percentFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const usdFormatter = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'USD' });

const formatSecondsFromMs = ms => {
  const val = Number(ms || 0);
  if (!Number.isFinite(val) || val <= 0) return '0,0 s';
  const seconds = val / 1000;
  return `${decimalFormatter.format(seconds)} s`;
};

const formatSeconds = seconds => {
  const val = Number(seconds || 0);
  if (!Number.isFinite(val) || val <= 0) return '0,0 s';
  return `${decimalFormatter.format(val)} s`;
};

const formatTokens = value => {
  const v = Number(value || 0);
  return v.toLocaleString('de-DE');
};

const formatUsd = value => {
  const v = Number(value || 0);
  if (!Number.isFinite(v) || v <= 0) return '$0.00';
  return usdFormatter.format(v);
};

const AgentSessionStatistics = () => {
  const theme = useTheme();
  const authCtx = useContext(AuthContext);
  const { fetchRatedSessions, loadSessionMessages } = useAgent();

  const [ratedPositive, setRatedPositive] = useState([]);
  const [ratedNegative, setRatedNegative] = useState([]);
  const [isFetchingRated, setIsFetchingRated] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerSession, setViewerSession] = useState(null);
  const [viewerMessages, setViewerMessages] = useState([]);
  const [appliedFilter, setAppliedFilter] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
  });
  const [startDate, setStartDate] = useState(dayjs(defaultStartDate));
  const [endDate, setEndDate] = useState(dayjs(defaultEndDate));
  const [chartType, setChartType] = useState('line');
  const [selectedEntryId, setSelectedEntryId] = useState(null);

  const fetchRatedSessionsRef = useRef(fetchRatedSessions);
  const loadSessionMessagesRef = useRef(loadSessionMessages);
  useEffect(() => {
    fetchRatedSessionsRef.current = fetchRatedSessions;
  }, [fetchRatedSessions]);
  useEffect(() => {
    loadSessionMessagesRef.current = loadSessionMessages;
  }, [loadSessionMessages]);

  const filterParams = useMemo(
    () => ({
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      branchScope: 'all',
    }),
    [appliedFilter]
  );

  const {
    payload: stats,
    isStale,
    cacheComputedAt,
    cacheExpiresAt,
    status,
    job,
    error,
    loadCacheEntry,
    refresh: refreshStatistic,
  } = useStatisticJob({
    statisticType: STATISTIC_TYPE_AGENT_SESSION,
    endpoint: '/api/v1/statistic/agentSessions',
    filterParams,
  });

  const { entries: cacheEntries, isLoading: entriesLoading } = useStatisticCacheEntries({
    statisticType: STATISTIC_TYPE_AGENT_SESSION,
  });

  const isFetchingStats = status === 'fetching' || status === 'pending' || status === 'running';

  const statSurfaceBg = useMemo(() => getStatisticsSurfaceBackground(theme), [theme]);
  const chartGridStroke = useMemo(() => getCartesianGridStroke(theme), [theme]);
  const axisStroke = useMemo(() => getRechartsAxisStroke(theme), [theme]);
  const chartTooltipProps = useMemo(() => getRechartsTooltipProps(theme), [theme]);
  const chartLegendBarProps = useMemo(() => getRechartsLegendBarProps(theme), [theme]);
  const chartLegendPieProps = useMemo(() => getRechartsLegendPieProps(theme), [theme]);
  const pieSliceStroke = useMemo(() => getPieSliceStroke(theme), [theme]);
  const barTooltipCursor = useMemo(() => getBarTooltipCursor(theme), [theme]);
  const kpiLabelColor = useMemo(() => getStatisticsKpiLabelColor(theme), [theme]);
  const kpiHelperColor = useMemo(() => getStatisticsKpiHelperColor(theme), [theme]);

  useEffect(() => {
    if (status === 'failed' && error) toast.error(error);
  }, [status, error]);

  const applyFilters = useCallback(
    (nextStart, nextEnd) => {
      const selectedStart = nextStart || startDate;
      const selectedEnd = nextEnd || endDate;
      if (
        !selectedStart ||
        !selectedEnd ||
        !selectedStart.isValid() ||
        !selectedEnd.isValid()
      ) {
        toast.error('Bitte wählen Sie gültige Daten aus');
        return;
      }
      if (selectedStart.isAfter(selectedEnd)) {
        toast.error('Das Startdatum darf nicht nach dem Enddatum liegen');
        return;
      }
      setStartDate(selectedStart);
      setEndDate(selectedEnd);
      setSelectedEntryId(null);
      setAppliedFilter({
        startDate: selectedStart.format('YYYY-MM-DD'),
        endDate: selectedEnd.format('YYYY-MM-DD'),
      });
    },
    [startDate, endDate]
  );

  useEffect(() => {
    setStartDate(dayjs(appliedFilter.startDate));
    setEndDate(dayjs(appliedFilter.endDate));
  }, [appliedFilter]);

  useEffect(() => {
    const loadRated = async () => {
      setIsFetchingRated(true);
      try {
        const [pos, neg] = await Promise.all([
          fetchRatedSessionsRef.current({
            from: appliedFilter.startDate,
            to: appliedFilter.endDate,
            helpful: true,
            limit: 50,
            skip: 0,
          }),
          fetchRatedSessionsRef.current({
            from: appliedFilter.startDate,
            to: appliedFilter.endDate,
            helpful: false,
            limit: 50,
            skip: 0,
          }),
        ]);
        setRatedPositive(Array.isArray(pos?.sessions) ? pos.sessions : []);
        setRatedNegative(Array.isArray(neg?.sessions) ? neg.sessions : []);
      } catch (e) {
        toast.error('Fehler beim Laden der bewerteten Chats');
        throw e;
      } finally {
        setIsFetchingRated(false);
      }
    };
    loadRated();
  }, [appliedFilter]);

  const openViewer = useCallback(async sessionToken => {
    setViewerOpen(true);
    setViewerLoading(true);
    setViewerMessages([]);
    setViewerSession(null);
    try {
      const data = await loadSessionMessagesRef.current(sessionToken);
      setViewerSession(data);
      const raw = Array.isArray(data?.messages) ? data.messages : [];
      const filtered = raw
        .filter(m => m.role !== 'system' && m.role !== 'tool')
        .map(m =>
          m.role === 'assistant' ? { ...m, senderName: m.senderName || 'AI-Assistent' } : m
        );
      setViewerMessages(filtered);
    } catch (e) {
      toast.error('Fehler beim Laden des Chats');
      throw e;
    } finally {
      setViewerLoading(false);
    }
  }, []);

  const handleChartTypeChange = (_e, value) => {
    if (value) setChartType(value);
  };

  const handleSelectEntry = useCallback(
    entryId => {
      setSelectedEntryId(entryId || null);
      if (entryId) loadCacheEntry(entryId);
    },
    [loadCacheEntry]
  );

  const chartData = useMemo(() => {
    const daily = stats?.timeSeries?.daily || [];
    if (!Array.isArray(daily)) return [];

    if (chartType === 'line') {
      return daily.map(point => ({
        ...point,
        label: dayjs(point.date).format('DD.MM.'),
        avgWaitSeconds:
          typeof point.avgWaitMs === 'number' && Number.isFinite(point.avgWaitMs)
            ? point.avgWaitMs / 1000
            : 0,
      }));
    }

    const start = dayjs(stats?.appliedFilter?.startDate || appliedFilter.startDate);
    const end = dayjs(stats?.appliedFilter?.endDate || appliedFilter.endDate);
    const diffDays = end.diff(start, 'day') + 1;

    let granularity = 'day';
    if (diffDays > 180) granularity = 'month';
    else if (diffDays > 35) granularity = 'week';

    if (granularity === 'day') {
      return daily.map(point => ({
        ...point,
        label: dayjs(point.date).format('DD.MM.'),
        avgWaitSeconds:
          typeof point.avgWaitMs === 'number' && Number.isFinite(point.avgWaitMs)
            ? point.avgWaitMs / 1000
            : 0,
      }));
    }

    const aggregated = {};
    daily.forEach(point => {
      const date = dayjs(point.date);
      let key;
      let label;

      if (granularity === 'week') {
        const weekStart = date.startOf('isoWeek');
        const weekEnd = date.endOf('isoWeek');
        key = weekStart.format('YYYY-MM-DD');
        label = `${weekStart.format('DD.MM.')} – ${weekEnd.format('DD.MM.')}`;
      } else {
        key = date.format('YYYY-MM');
        label = date.format('MMM YYYY');
      }

      if (!aggregated[key]) {
        aggregated[key] = {
          date: key,
          label,
          sessions: 0,
          messages: 0,
          tokensTotal: 0,
          aiMS: 0,
          backendMS: 0,
          avgWaitMsSum: 0,
          avgWaitMsSamples: 0,
        };
      }

      aggregated[key].sessions += point.sessions || 0;
      aggregated[key].messages += point.messages || 0;
      aggregated[key].tokensTotal += point.tokensTotal || 0;
      aggregated[key].aiMS += point.aiMS || 0;
      aggregated[key].backendMS += point.backendMS || 0;
      if (typeof point.avgWaitMs === 'number' && Number.isFinite(point.avgWaitMs)) {
        aggregated[key].avgWaitMsSum += point.avgWaitMs;
        aggregated[key].avgWaitMsSamples += 1;
      }
    });

    return Object.values(aggregated)
      .map(v => ({
        ...v,
        avgWaitMs: v.avgWaitMsSamples > 0 ? v.avgWaitMsSum / v.avgWaitMsSamples : 0,
        avgWaitSeconds: v.avgWaitMsSamples > 0 ? v.avgWaitMsSum / v.avgWaitMsSamples / 1000 : 0,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [stats, chartType, appliedFilter]);

  const hasChartData = chartData.length > 0;

  const topicsPie = useMemo(() => {
    const topics = stats?.totals?.topics || [];
    if (!Array.isArray(topics)) return [];
    return topics
      .filter(t => (t?.count || 0) > 0)
      .map(t => ({ name: t.label || t.key, value: t.count }))
      .slice(0, 10);
  }, [stats]);

  const modelPie = useMemo(() => {
    const models = stats?.totals?.modelUsage || [];
    if (!Array.isArray(models)) return [];
    return models
      .filter(m => (m?.totalTokens || 0) > 0)
      .slice(0, 10)
      .map(m => ({ name: m.model || 'unknown', value: m.totalTokens || 0 }));
  }, [stats]);

  const cards = useMemo(() => {
    const t = stats?.totals || null;
    if (!t) return [];
    const cost = t.costEstimate || {};
    return [
      {
        label: 'Sessions',
        value: (t.sessions || 0).toLocaleString('de-DE'),
        helper: 'Anzahl Agent Sessions im Zeitraum',
      },
      {
        label: 'Eskalationsrate',
        value: t.escalations?.escalationRate
          ? `${percentFormatter.format(t.escalations.escalationRate * 100)} %`
          : '0,0 %',
        helper: 'Anteil eskalierter Sessions',
      },
      {
        label: 'Ø Antwortzeit (p50)',
        value: formatSecondsFromMs(t.responseTimeMs?.p50),
        helper: 'Median Zeit von User → Assistant',
      },
      {
        label: 'Antwortzeit (p95)',
        value: formatSecondsFromMs(t.responseTimeMs?.p95),
        helper: '95%-Perzentil Zeit von User → Assistant',
      },
      {
        label: 'Tokens gesamt',
        value: formatTokens(t.tokenUsage?.total),
        helper: 'Summe Prompt + Completion Tokens',
      },
      {
        label: 'Kosten (Schätzung)',
        value: cost?.estimated ? formatUsd(cost.estimated) : '—',
        helper: cost?.unknownTokenCount
          ? `Unbekannte Token: ${formatTokens(cost.unknownTokenCount)}`
          : 'Auf Basis der Modell-Calls',
      },
      {
        label: 'Sessions mit Buchung gefunden',
        value: (t.identification?.sessionsWithCandidateToken || 0).toLocaleString('de-DE'),
        helper: 'Sessions mit candidateSearchToken',
      },
      {
        label: 'Abusive Strikes',
        value: (t.abusive?.totalStrikes || 0).toLocaleString('de-DE'),
        helper: `Sessions betroffen: ${(t.abusive?.sessionsWithStrikes || 0).toLocaleString('de-DE')}`,
      },
    ];
  }, [stats]);

  if (!authCtx.checkRight('agent:stats')) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <div style={getStatisticsPageRootStyle(theme)}>
      <StatisticViewShell
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={applyFilters}
        applyDisabled={isFetchingStats}
        status={status}
        isStale={isStale}
        cacheComputedAt={cacheComputedAt}
        cacheExpiresAt={cacheExpiresAt}
        job={job}
        error={error}
        cacheEntries={cacheEntries}
        selectedEntryId={selectedEntryId}
        onSelectEntry={handleSelectEntry}
        entriesLoading={entriesLoading}
        onRefresh={refreshStatistic}
        hasPayload={!!stats}
      >

      <section>
        <h2 style={{ marginBottom: '0.5rem' }}>Kennzahlen</h2>
        {isFetchingStats ? (
          <p>Lade Kennzahlen …</p>
        ) : stats ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
            }}
          >
            {cards.map(card => (
              <Box
                component='article'
                key={card.label}
                sx={{
                  bgcolor: statSurfaceBg,
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography component='span' variant='body2' sx={{ color: kpiLabelColor, fontSize: '0.85rem' }}>
                  {card.label}
                </Typography>
                <Typography component='strong' sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                  {card.value}
                </Typography>
                {card.helper ? (
                  <Typography component='small' variant='caption' sx={{ color: kpiHelperColor }}>
                    {card.helper}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Box>
        ) : (
          <p>Keine Daten für den ausgewählten Zeitraum vorhanden.</p>
        )}
      </section>

      {stats && (
        <section>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent='space-between'
            sx={{ mb: 1 }}
          >
            <h2 style={{ margin: 0 }}>Sessions & Tokens im Zeitverlauf</h2>
            <ToggleButtonGroup
              value={chartType}
              exclusive
              size='small'
              onChange={handleChartTypeChange}
            >
              <ToggleButton value='line'>Linie</ToggleButton>
              <ToggleButton value='bar'>Balken</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {hasChartData ? (
            <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 360 }}>
              <ResponsiveContainer width='100%' height='100%'>
                {chartType === 'line' ? (
                  <LineChart
                    data={chartData}
                    margin={{
                      left: 8,
                      right: 8,
                      top: 8,
                      bottom: 0,
                    }}
                  >
                    <CartesianGrid stroke={chartGridStroke} />
                    <XAxis dataKey='label' stroke={axisStroke} />
                    <YAxis
                      yAxisId='sessions'
                      stroke='#4caf50'
                      tick={{ fill: '#4caf50' }}
                      axisLine={{ stroke: '#4caf50' }}
                      tickMargin={6}
                    />
                    <YAxis
                      yAxisId='tokens'
                      orientation='right'
                      stroke='#2196f3'
                      tick={{ fill: '#2196f3' }}
                      axisLine={{ stroke: '#2196f3' }}
                      tickFormatter={v => `${Math.round(Number(v || 0) / 1000)}k`}
                      tickMargin={6}
                      width={62}
                    />
                    <YAxis
                      yAxisId='seconds'
                      orientation='right'
                      mirror
                      stroke='#ff9800'
                      tick={{ fill: '#ff9800' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={v => formatSeconds(v)}
                      tickMargin={6}
                    />
                    <Tooltip
                      {...chartTooltipProps}
                      formatter={(value, name) => {
                        if (name === 'Tokens') return [formatTokens(value), name];
                        if (name === 'Ø Antwortzeit (s)') return [formatSeconds(value), name];
                        return [formatTokens(value), name];
                      }}
                    />
                    <Legend {...chartLegendBarProps} />
                    <Line
                      yAxisId='sessions'
                      type='monotone'
                      dataKey='sessions'
                      name='Sessions'
                      stroke='#4caf50'
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      yAxisId='tokens'
                      type='monotone'
                      dataKey='tokensTotal'
                      name='Tokens'
                      stroke='#2196f3'
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      yAxisId='seconds'
                      type='monotone'
                      dataKey='avgWaitSeconds'
                      name='Ø Antwortzeit (s)'
                      stroke='#ff9800'
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                ) : (
                  <BarChart
                    data={chartData}
                    margin={{
                      left: 8,
                      right: 8,
                      top: 8,
                      bottom: 0,
                    }}
                  >
                    <CartesianGrid stroke={chartGridStroke} vertical={false} />
                    <XAxis dataKey='label' stroke={axisStroke} />
                    <YAxis
                      yAxisId='tokens'
                      orientation='right'
                      stroke='#2196f3'
                      tick={{ fill: '#2196f3' }}
                      axisLine={{ stroke: '#2196f3' }}
                      tickFormatter={v => `${Math.round(Number(v || 0) / 1000)}k`}
                      tickMargin={6}
                      width={62}
                    />
                    <YAxis
                      yAxisId='sessions'
                      stroke='#4caf50'
                      tick={{ fill: '#4caf50' }}
                      axisLine={{ stroke: '#4caf50' }}
                      tickMargin={6}
                    />
                    <YAxis
                      yAxisId='seconds'
                      orientation='right'
                      mirror
                      stroke='#ff9800'
                      tick={{ fill: '#ff9800' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={v => formatSeconds(v)}
                      tickMargin={6}
                    />
                    <Tooltip
                      cursor={barTooltipCursor}
                      {...chartTooltipProps}
                      formatter={(value, name) => {
                        if (name === 'Tokens') return [formatTokens(value), name];
                        if (name === 'Ø Antwortzeit (s)') return [formatSeconds(value), name];
                        return [formatTokens(value), name];
                      }}
                    />
                    <Legend {...chartLegendBarProps} />
                    <Bar yAxisId='sessions' dataKey='sessions' name='Sessions' fill='#4caf50' />
                    <Bar yAxisId='tokens' dataKey='tokensTotal' name='Tokens' fill='#2196f3' />
                    <Bar
                      yAxisId='seconds'
                      dataKey='avgWaitSeconds'
                      name='Ø Antwortzeit (s)'
                      fill='#ff9800'
                    />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </Paper>
          ) : (
            <p>Keine Zeitreihendaten verfügbar.</p>
          )}
        </section>
      )}

      {stats && (
        <section>
          <h2 style={{ marginBottom: '1rem' }}>Themen (Intent) & Modell-Verteilung</h2>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  bgcolor: statSurfaceBg,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <h3>Themen</h3>
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={topicsPie}
                        cx='50%'
                        cy='50%'
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey='value'
                        stroke={pieSliceStroke}
                      >
                        {topicsPie.map((_, idx) => (
                          <Cell key={`topic-${idx}`} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip {...chartTooltipProps} formatter={value => `${formatTokens(value)} Sessions`} />
                      <Legend {...chartLegendPieProps} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  bgcolor: statSurfaceBg,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <h3>Modelle (Tokens)</h3>
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={modelPie}
                        cx='50%'
                        cy='50%'
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey='value'
                        stroke={pieSliceStroke}
                      >
                        {modelPie.map((_, idx) => (
                          <Cell key={`model-${idx}`} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip {...chartTooltipProps} formatter={value => `${formatTokens(value)} Tokens`} />
                      <Legend {...chartLegendPieProps} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Paper>
            </Grid>
          </Grid>
        </section>
      )}

      {stats && (
        <section>
          <h2 style={{ marginBottom: '1rem' }}>Bewertete Chats</h2>
          {isFetchingRated ? (
            <p>Lade Bewertungen …</p>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg }}>
                  <h3 style={{ marginTop: 0 }}>Positive Chats anzeigen</h3>
                  {ratedPositive.length === 0 ? (
                    <Typography variant='body2' color='text.secondary'>
                      Keine positiven Bewertungen im Zeitraum.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {ratedPositive.map(s => (
                        <Paper
                          key={s.sessionToken}
                          elevation={0}
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.text.primary, 0.05),
                            border: 1,
                            borderColor: 'divider',
                            cursor: 'pointer',
                          }}
                          onClick={() => openViewer(s.sessionToken)}
                        >
                          <Typography variant='caption' color='text.secondary'>
                            {s.createdAt ? dayjs(s.createdAt).format('DD.MM.YYYY HH:mm') : '—'}
                          </Typography>
                          <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            {s.preview || 'Keine Vorschau'}
                          </Typography>
                          {s.rating?.comment ? (
                            <Typography variant='caption' color='text.secondary'>
                              Kommentar: {s.rating.comment}
                            </Typography>
                          ) : null}
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg }}>
                  <h3 style={{ marginTop: 0 }}>Negative Chats anzeigen</h3>
                  {ratedNegative.length === 0 ? (
                    <Typography variant='body2' color='text.secondary'>
                      Keine negativen Bewertungen im Zeitraum.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {ratedNegative.map(s => (
                        <Paper
                          key={s.sessionToken}
                          elevation={0}
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.text.primary, 0.05),
                            border: 1,
                            borderColor: 'divider',
                            cursor: 'pointer',
                          }}
                          onClick={() => openViewer(s.sessionToken)}
                        >
                          <Typography variant='caption' color='text.secondary'>
                            {s.createdAt ? dayjs(s.createdAt).format('DD.MM.YYYY HH:mm') : '—'}
                          </Typography>
                          <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            {s.preview || 'Keine Vorschau'}
                          </Typography>
                          {s.rating?.comment ? (
                            <Typography variant='caption' color='text.secondary'>
                              Kommentar: {s.rating.comment}
                            </Typography>
                          ) : null}
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </section>
      )}

      {viewerOpen && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            inset: { xs: 0, md: '10% 10%' },
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: { xs: 0, md: 2 },
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'background.default',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant='subtitle1' fontWeight={700}>
                Chat
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {viewerSession?.sessionToken || ''}
              </Typography>
            </Box>
            <Button variant='outlined' onClick={() => setViewerOpen(false)}>
              Schließen
            </Button>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              p: 2,
              bgcolor:
                theme.palette.mode === 'light'
                  ? alpha(theme.palette.common.black, 0.03)
                  : alpha(theme.palette.common.black, 0.2),
            }}
          >
            {viewerLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ height: '100%' }}>
                {/* reuse existing chat renderer */}
                {viewerMessages.length ? (
                  <Box sx={{ height: '100%' }}>
                    {/* eslint-disable-next-line react/jsx-pascal-case */}
                    {/*
                      Importing ChatConversation here would add another dependency surface.
                      Keep a minimal message reader using existing styling in this view.
                    */}
                    <Box sx={{ height: '100%', overflow: 'auto' }}>
                      {viewerMessages.map((m, idx) => (
                        <Paper
                          key={`${idx}:${m.createdAt || ''}`}
                          elevation={0}
                          sx={{
                            p: 1.25,
                            mb: 1,
                            borderRadius: 2,
                            backgroundColor:
                              m.role === 'assistant'
                                ? alpha(theme.palette.success.main, 0.15)
                                : alpha(theme.palette.text.primary, 0.06),
                            border: 1,
                            borderColor: 'divider',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          <Typography variant='caption' color='text.secondary'>
                            {(m.role === 'assistant' ? 'Assistent' : 'User') +
                              (m.createdAt
                                ? ` • ${dayjs(m.createdAt).format('DD.MM.YYYY HH:mm')}`
                                : '')}
                          </Typography>
                          <Typography variant='body2' sx={{ mt: 0.5 }}>
                            {m.content}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant='body2' color='text.secondary'>
                    Keine Nachrichten.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      )}
      </StatisticViewShell>
    </div>
  );
};

export default AgentSessionStatistics;
